import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { seoApi } from '../../services/seoApi';
import { magazineApi } from '../../services/magazineApi';
import { LoadingState, EmptyState } from '../Common/PanelState';
import ConfirmationModal from '../Common/ConfirmationModal';
import { useSeo } from '../Common/SeoHead.js';

const FLARE_RED = '#c4151c';

const Icon = ({ path, className = 'h-5 w-5' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
);

function countColor(n, goodMin, goodMax, hardMax) {
  if (n > hardMax) return '#dc2626';
  if (n > goodMax || (goodMin && n > 0 && n < goodMin)) return '#d97706';
  if (n === 0) return '#9ca3af';
  return '#059669';
}

function fieldStatus(n, goodMin, goodMax, hardMax) {
  if (!n) return 'empty';
  if (n > hardMax) return 'over';
  if (n < goodMin) return 'short';
  if (n > goodMax) return 'long';
  return 'good';
}

function statusLabel(status) {
  return ({
    empty: 'Missing',
    over: 'Too long',
    short: 'Too short',
    long: 'A bit long',
    good: 'Good',
  })[status] || status;
}

function snippetTitle(title, suffix) {
  const t = String(title || '').trim();
  const s = String(suffix || 'Flare Digital').trim() || 'Flare Digital';
  if (!t) return s;
  if (t.toLowerCase().endsWith(`| ${s}`.toLowerCase()) || t.toLowerCase().endsWith(s.toLowerCase())) return t;
  return `${t} | ${s}`;
}

function shopPath(path) {
  if (!path || String(path).includes(':')) return path || '/';
  return path === '/' ? '/' : path;
}

function copyText(value, okMsg) {
  const text = String(value || '');
  if (!text) {
    toast.info('Nothing to copy');
    return;
  }
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(() => toast.success(okMsg)).catch(() => toast.error('Copy failed'));
    return;
  }
  toast.error('Clipboard is not available');
}

function StatCard({ label, value, hint, icon, iconBg }) {
  return (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-lg flex items-center justify-center text-white shadow-sm shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <Icon path={icon} className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-gray-500 tracking-wide">{label}</div>
          <div className="text-2xl font-extrabold text-gray-900 leading-tight truncate" title={String(value)}>
            {value}
          </div>
          {hint ? <div className="text-xs text-gray-400 mt-0.5">{hint}</div> : null}
        </div>
      </div>
    </div>
  );
}

function CharHint({ value, goodMin, goodMax, hardMax }) {
  const n = String(value || '').length;
  const status = fieldStatus(n, goodMin, goodMax, hardMax);
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: countColor(n, goodMin, goodMax, hardMax) }}>
        {statusLabel(status)}
      </span>
      <span className="text-xs font-semibold tabular-nums" style={{ color: countColor(n, goodMin, goodMax, hardMax) }}>
        {n}/{hardMax}
      </span>
    </span>
  );
}

function ScoreBar({ value, goodMin, goodMax, hardMax }) {
  const n = String(value || '').length;
  const pct = Math.min(100, Math.round((n / hardMax) * 100));
  return (
    <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 overflow-hidden">
      <div
        className="h-full rounded-full transition-all"
        style={{ width: `${pct}%`, backgroundColor: countColor(n, goodMin, goodMax, hardMax) }}
      />
    </div>
  );
}

function KeywordChips({ value, onChange, max }) {
  const [draft, setDraft] = useState('');
  const chips = String(value || '').split(',').map((k) => k.trim()).filter(Boolean);

  const emit = (next) => onChange(next.join(', ').slice(0, max));

  const add = () => {
    const piece = draft.trim().replace(/,/g, '');
    if (!piece) return;
    if (chips.some((c) => c.toLowerCase() === piece.toLowerCase())) {
      setDraft('');
      return;
    }
    emit([...chips, piece]);
    setDraft('');
  };

  return (
    <div className="mt-1 rounded-lg border border-gray-300 bg-white px-2 py-2 focus-within:ring-2 focus-within:ring-red-200 focus-within:border-red-400">
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip) => (
          <span key={chip} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 text-xs font-semibold border border-rose-100">
            {chip}
            <button
              type="button"
              onClick={() => emit(chips.filter((c) => c !== chip))}
              className="text-rose-500 hover:text-rose-800"
              aria-label={`Remove ${chip}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add();
            }
            if (e.key === 'Backspace' && !draft && chips.length) emit(chips.slice(0, -1));
          }}
          onBlur={add}
          placeholder={chips.length ? 'Add keyword' : 'fashion, lifestyle, pakistan'}
          className="min-w-[140px] flex-1 text-sm outline-none py-0.5 px-1"
        />
      </div>
    </div>
  );
}

function SerpPreview({ title, url, description, mobile }) {
  const crumbs = String(url || '').replace(/^https?:\/\//, '').split('/').filter(Boolean);
  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-4 ${mobile ? 'max-w-[360px]' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-7 h-7 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center text-[10px] font-black text-rose-700">F</div>
        <div className="min-w-0">
          <div className="text-sm text-gray-800 truncate">Flare Digital</div>
          <div className="text-xs text-[#006621] truncate">
            {crumbs[0] || 'digital.flare.pk'}
            {crumbs.slice(1).length ? ` › ${crumbs.slice(1).join(' › ')}` : ''}
          </div>
        </div>
      </div>
      <div className="text-[#1a0dab] text-lg leading-snug font-medium hover:underline cursor-default">{title}</div>
      <div className="text-sm text-gray-600 mt-1 line-clamp-2">{description || 'Add a meta description to control this snippet.'}</div>
    </div>
  );
}

function formFromPage(page) {
  return {
    title: page.title || '',
    description: page.description || '',
    keywords: page.keywords || '',
  };
}

function defaultsFromPage(page) {
  return {
    title: page.defaults?.title || page.title || '',
    description: page.index ? (page.defaults?.description || '') : '',
    keywords: page.index ? (page.defaults?.keywords || '') : '',
  };
}

export default function SeoSection() {
  const liveSeo = useSeo();
  const [payload, setPayload] = useState(null);
  const [suffix, setSuffix] = useState('Flare Digital');
  const [pages, setPages] = useState({});
  const [savedSnap, setSavedSnap] = useState('');
  const [tab, setTab] = useState('indexed');
  const [query, setQuery] = useState('');
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openId, setOpenId] = useState('home');
  const [serpMode, setSerpMode] = useState('desktop');
  const [resetOpen, setResetOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [issues, setIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState('');
  const [tools, setTools] = useState(null);
  const [toolsLoading, setToolsLoading] = useState(false);

  const applyPayload = (data) => {
    setPayload(data);
    const nextSuffix = data.suffix || 'Flare Digital';
    const next = {};
    Object.entries(data.pages || {}).forEach(([id, page]) => {
      next[id] = formFromPage(page);
    });
    setSuffix(nextSuffix);
    setPages(next);
    setSavedSnap(JSON.stringify({ suffix: nextSuffix, pages: next }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await seoApi.get();
      applyPayload(data);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load SEO');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = savedSnap !== '' && JSON.stringify({ suffix, pages }) !== savedSnap;

  useEffect(() => {
    const onLeave = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [dirty]);

  const limits = payload?.limits || {
    title: { good_min: 50, good_max: 60, max: 70 },
    description: { good_min: 150, good_max: 160, max: 180 },
    keywords: { good_min: 20, good_max: 120, max: 120 },
  };

  const catalog = useMemo(() => Object.values(payload?.pages || {}), [payload]);
  const origin = (typeof window !== 'undefined' ? window.location.origin : (payload?.origin || '')).replace(/\/+$/, '');

  const needsWork = (page, form) => {
    if (!page.index) return false;
    const title = String(form?.title || '').trim();
    const desc = String(form?.description || '').trim();
    if (!title || !desc) return true;
    if (title.length > limits.title.max) return true;
    if (desc.length > limits.description.max) return true;
    return false;
  };

  const indexed = catalog.filter((p) => p.index);
  const noindex = catalog.filter((p) => !p.index);
  const attentionCount = indexed.filter((p) => needsWork(p, pages[p.id])).length;

  const list = (tab === 'noindex' ? noindex : indexed).filter((page) => {
    const form = pages[page.id] || formFromPage(page);
    const hay = `${page.label} ${page.path} ${form.title} ${form.description} ${form.keywords}`.toLowerCase();
    if (query.trim() && !hay.includes(query.trim().toLowerCase())) return false;
    if (onlyAttention && tab === 'indexed' && !needsWork(page, form)) return false;
    return true;
  });

  const loadIssues = useCallback(async () => {
    setIssuesLoading(true);
    setIssuesError('');
    try {
      const published = await magazineApi.getPublishedIssues({ per_page: 100 });
      const formatted = published.data?.data_formatted;
      if (Array.isArray(formatted) && formatted.length) {
        setIssues(formatted);
        return;
      }
      const { data } = await magazineApi.getIssues({ per_page: 100 });
      const rows = Array.isArray(data?.data) ? data.data : [];
      setIssues(rows.map((row) => magazineApi.formatIssue(row)));
    } catch (e) {
      const msg = e?.response?.data?.message || 'Could not load issues';
      setIssuesError(msg);
      toast.error(msg);
    } finally {
      setIssuesLoading(false);
    }
  }, []);

  const loadTools = useCallback(async () => {
    setToolsLoading(true);
    try {
      const { data } = await seoApi.tools();
      setTools(data);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not load robots/sitemap');
    } finally {
      setToolsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'issues' && issues.length === 0) loadIssues();
    if (tab === 'tools' && !tools) loadTools();
  }, [tab, issues.length, tools, loadIssues, loadTools]);

  const patch = (id, field, value, max) => {
    setPages((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: String(value).slice(0, max) },
    }));
  };

  const resetPage = (page) => {
    setPages((prev) => ({ ...prev, [page.id]: defaultsFromPage(page) }));
    toast.info(`${page.label} restored to defaults`);
  };

  const resetAllIndexed = () => {
    setPages((prev) => {
      const next = { ...prev };
      indexed.forEach((page) => {
        next[page.id] = defaultsFromPage(page);
      });
      return next;
    });
    setResetOpen(false);
    toast.info('Indexed pages restored to defaults. Save to apply.');
  };

  const discardChanges = () => {
    if (!payload) return;
    applyPayload(payload);
    setDiscardOpen(false);
    toast.info('Unsaved edits discarded');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await seoApi.update({ suffix, pages });
      applyPayload(data);
      liveSeo?.refresh?.();
      toast.success('SEO saved.');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const missingIssueSeo = issues.filter((issue) => !String(issue.seo_description || '').trim()).length;

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <LoadingState label="Loading SEO" />
      </div>
    );
  }

  const tabs = [
    { id: 'indexed', label: 'Indexed pages', count: indexed.length },
    { id: 'noindex', label: 'Noindex titles', count: noindex.length },
    { id: 'issues', label: 'Magazine issues', count: issues.length || null },
    { id: 'tools', label: 'Robots & sitemap' },
  ];

  return (
    <div className="space-y-6 pb-24">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="admin-page-title">SEO</h1>
          <p className="admin-page-sub">
            Control titles, descriptions, and keywords for public pages. Private admin pages stay noindex.
            Format: <span className="font-semibold text-gray-800">Page title | {suffix || 'Flare Digital'}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 font-semibold text-sm"
          >
            <Icon path="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" className="h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50 shadow-sm"
            style={{ backgroundColor: FLARE_RED }}
          >
            <Icon path="M5 13l4 4L19 7" className="h-4 w-4" />
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Indexed"
          value={indexed.length}
          hint="Public shop pages"
          icon="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          iconBg={FLARE_RED}
        />
        <StatCard
          label="Noindex"
          value={noindex.length}
          hint="Admin & private"
          icon="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M3 3l18 18"
          iconBg="#4f46e5"
        />
        <StatCard
          label="Needs work"
          value={attentionCount}
          hint="Empty or over the limit"
          icon="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          iconBg="#d97706"
        />
        <StatCard
          label="Brand suffix"
          value={suffix || 'Flare Digital'}
          hint={`${String(suffix || '').length}/40 characters`}
          icon="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          iconBg="#059669"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <label className="block flex-1">
            <span className="text-xs font-semibold text-gray-600">Brand suffix</span>
            <input
              value={suffix}
              onChange={(e) => setSuffix(e.target.value.slice(0, 40))}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              Appended to every title, e.g. Pakistan&apos;s Best Read Magazine | {suffix || 'Flare Digital'}
            </p>
          </label>
          <button
            type="button"
            onClick={() => setResetOpen(true)}
            className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 self-start"
          >
            Restore indexed defaults
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col xl:flex-row xl:items-center gap-3">
          <div className="inline-flex flex-wrap rounded-lg bg-gray-100 p-1">
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  setTab(item.id);
                  if (item.id === 'issues' && issues.length === 0) setIssuesLoading(true);
                  if (item.id === 'tools' && !tools) setToolsLoading(true);
                }}
                className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${
                  tab === item.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {item.label}
                {item.count != null ? <span className="ml-1 text-xs text-gray-400">{item.count}</span> : null}
              </button>
            ))}
          </div>
          {(tab === 'indexed' || tab === 'noindex') ? (
            <div className="flex flex-1 flex-wrap items-center gap-2">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search pages, titles, keywords…"
                className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
              />
              {tab === 'indexed' ? (
                <button
                  type="button"
                  onClick={() => setOnlyAttention((v) => !v)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                    onlyAttention ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-white text-gray-700 border-gray-200'
                  }`}
                >
                  Needs work ({attentionCount})
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {(tab === 'indexed' || tab === 'noindex') && list.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100">
          <EmptyState title="No matching pages" hint="Clear search or turn off the needs-work filter." />
        </div>
      ) : null}

      {(tab === 'indexed' || tab === 'noindex') ? (
        <div className="space-y-3">
          {list.map((page) => {
            const form = pages[page.id] || formFromPage(page);
            const open = openId === page.id;
            const preview = snippetTitle(form.title, suffix);
            const previewUrl = `${origin}${shopPath(page.path) === '/' ? '/' : String(shopPath(page.path)).replace(':slug', 'issue-slug')}`;
            const attention = needsWork(page, form);
            const changed = JSON.stringify(form) !== JSON.stringify(formFromPage(page));
            return (
              <div key={page.id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${attention ? 'border-amber-200' : 'border-gray-100'}`}>
                <button
                  type="button"
                  onClick={() => setOpenId(open ? '' : page.id)}
                  className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left hover:bg-gray-50"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-gray-900">{page.label}</div>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${page.index ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {page.index ? 'index' : 'noindex'}
                      </span>
                      {attention ? (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">needs work</span>
                      ) : null}
                      {changed ? (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700">unsaved</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5 font-mono">{page.path}</div>
                  </div>
                  <span className={`text-gray-400 text-xl leading-none transition-transform ${open ? 'rotate-180' : ''}`}>▾</span>
                </button>
                {open ? (
                  <div className="px-5 pb-5 border-t border-gray-100 space-y-4 pt-4">
                    {page.fallback ? (
                      <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                        Used only when an issue has empty SEO fields. Edit live issue titles in Manage Issues.
                      </p>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => copyText(preview, 'Title copied')} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                        Copy title
                      </button>
                      {page.index ? (
                        <button type="button" onClick={() => copyText(form.description, 'Description copied')} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                          Copy description
                        </button>
                      ) : null}
                      <button type="button" onClick={() => copyText(previewUrl, 'URL copied')} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                        Copy URL
                      </button>
                      {!String(page.path).includes(':') ? (
                        <a
                          href={shopPath(page.path)}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Open page
                        </a>
                      ) : (
                        <Link to="/magazine/issues" className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                          Edit in Manage Issues
                        </Link>
                      )}
                      <button type="button" onClick={() => resetPage(page)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                        Reset to default
                      </button>
                    </div>
                    <label className="block text-sm">
                      <span className="flex justify-between font-semibold text-gray-800">
                        Title
                        <CharHint value={form.title} goodMin={limits.title.good_min} goodMax={limits.title.good_max} hardMax={limits.title.max} />
                      </span>
                      <input
                        value={form.title}
                        onChange={(e) => patch(page.id, 'title', e.target.value, limits.title.max)}
                        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
                      />
                      <ScoreBar value={form.title} goodMin={limits.title.good_min} goodMax={limits.title.good_max} hardMax={limits.title.max} />
                    </label>
                    {page.index ? (
                      <>
                        <label className="block text-sm">
                          <span className="flex justify-between font-semibold text-gray-800">
                            Meta description
                            <CharHint value={form.description} goodMin={limits.description.good_min} goodMax={limits.description.good_max} hardMax={limits.description.max} />
                          </span>
                          <textarea
                            rows={3}
                            value={form.description}
                            onChange={(e) => patch(page.id, 'description', e.target.value, limits.description.max)}
                            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
                          />
                          <ScoreBar value={form.description} goodMin={limits.description.good_min} goodMax={limits.description.good_max} hardMax={limits.description.max} />
                        </label>
                        <label className="block text-sm">
                          <span className="flex justify-between font-semibold text-gray-800">
                            Keywords
                            <CharHint value={form.keywords} goodMin={limits.keywords.good_min} goodMax={limits.keywords.good_max} hardMax={limits.keywords.max} />
                          </span>
                          <KeywordChips
                            value={form.keywords}
                            onChange={(next) => patch(page.id, 'keywords', next, limits.keywords.max)}
                            max={limits.keywords.max}
                          />
                          <ScoreBar value={form.keywords} goodMin={limits.keywords.good_min} goodMax={limits.keywords.good_max} hardMax={limits.keywords.max} />
                        </label>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Google preview</div>
                            <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                              {['desktop', 'mobile'].map((mode) => (
                                <button
                                  key={mode}
                                  type="button"
                                  onClick={() => setSerpMode(mode)}
                                  className={`px-2.5 py-1 text-xs font-semibold rounded-md capitalize ${serpMode === mode ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}
                                >
                                  {mode}
                                </button>
                              ))}
                            </div>
                          </div>
                          <SerpPreview title={preview} url={previewUrl} description={form.description} mobile={serpMode === 'mobile'} />
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                        This page is locked to noindex,nofollow. Search engines will not list it. Title is for the browser tab only.
                      </p>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {tab === 'issues' ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-gray-900">Issue SEO audit</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {issuesLoading ? 'Loading…' : `${missingIssueSeo} of ${issues.length} missing a meta description`}
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={loadIssues} className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50">
                Refresh issues
              </button>
              <Link to="/magazine/issues" className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: FLARE_RED }}>
                Manage Issues
              </Link>
            </div>
          </div>
          {issuesLoading ? <LoadingState label="Loading issues" /> : null}
          {!issuesLoading && issuesError ? (
            <EmptyState title="Could not load issues" hint={issuesError} />
          ) : null}
          {!issuesLoading && !issuesError && issues.length === 0 ? (
            <EmptyState title="No magazine issues" hint="Create issues in Manage Issues first." />
          ) : null}
          {!issuesLoading && !issuesError && issues.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Issue</th>
                    <th className="px-5 py-3 font-semibold">SEO title</th>
                    <th className="px-5 py-3 font-semibold">Description</th>
                    <th className="px-5 py-3 font-semibold">Keywords</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {issues.map((issue) => {
                    const desc = String(issue.seo_description || '').trim();
                    const keys = String(issue.seo_keywords || '').trim();
                    const title = String(issue.seo_title || issue.title || '').trim();
                    return (
                      <tr key={issue.id} className="hover:bg-gray-50">
                        <td className="px-5 py-3">
                          <div className="font-semibold text-gray-900">{issue.title || issue.month_year}</div>
                          <div className="text-xs text-gray-400 font-mono">/magazines/{issue.slug}</div>
                        </td>
                        <td className="px-5 py-3 text-gray-700 max-w-xs truncate">{title || '—'}</td>
                        <td className="px-5 py-3">
                          {desc ? (
                            <span className="text-emerald-700 font-semibold">{desc.length} chars</span>
                          ) : (
                            <span className="text-amber-700 font-semibold">Missing</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-gray-500">{keys || '—'}</td>
                        <td className="px-5 py-3">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${issue.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {issue.status || 'draft'}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          {issue.slug ? (
                            <a href={`/magazines/${issue.slug}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-rose-700 hover:underline">
                              View
                            </a>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === 'tools' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {toolsLoading ? (
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100">
              <LoadingState label="Loading crawl files" />
            </div>
          ) : null}
          {tools ? (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-gray-900">robots.txt</div>
                    <div className="text-xs text-gray-500 truncate">{tools.robots_url}</div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => copyText(tools.robots, 'robots.txt copied')} className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-semibold">Copy</button>
                    <a href={tools.robots_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-semibold">Open</a>
                  </div>
                </div>
                <pre className="p-5 text-xs text-gray-700 overflow-auto max-h-80 whitespace-pre-wrap font-mono">{tools.robots}</pre>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-gray-900">sitemap.xml</div>
                    <div className="text-xs text-gray-500 truncate">{tools.sitemap_url}</div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => copyText(tools.sitemap, 'sitemap copied')} className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-semibold">Copy</button>
                    <a href={tools.sitemap_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-semibold">Open</a>
                  </div>
                </div>
                <pre className="p-5 text-xs text-gray-700 overflow-auto max-h-80 whitespace-pre-wrap font-mono">{tools.sitemap}</pre>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {dirty ? (
        <div className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-xl bg-white border border-gray-200 shadow-lg px-4 py-3">
          <span className="text-sm font-semibold text-gray-800">Unsaved SEO changes</span>
          <button type="button" onClick={() => setDiscardOpen(true)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            Discard
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold"
            style={{ backgroundColor: FLARE_RED }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      ) : null}

      <ConfirmationModal
        isOpen={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={resetAllIndexed}
        title="Restore indexed defaults?"
      >
        Indexed shop titles, descriptions, and keywords will reset to the original Flare copy. Noindex titles stay as they are. Save afterwards to keep the change.
      </ConfirmationModal>
      <ConfirmationModal
        isOpen={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={discardChanges}
        title="Discard unsaved changes?"
      >
        Title, description, keyword, and suffix edits that have not been saved will be lost.
      </ConfirmationModal>
    </div>
  );
}
